import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { reinitializeDataverseClient } from '../api';
import { useAuth } from './AuthProvider';

export type OrgId = 'prod' | 'dev';

interface OrgContextValue {
  activeOrg: OrgId;
  setActiveOrg: (org: OrgId) => void;
  orgLabel: string;
  prodUrl: string;
  devUrl: string;
  hasDevOrg: boolean;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrgContext must be used within DataverseOrgProvider');
  return ctx;
}

function getStorageKey(appId?: string): string {
  return appId ? `vera-${appId}-active-org` : 'vera-active-org';
}

function getOrgLabel(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\.crm.*$/, '');
}

interface DataverseOrgProviderProps {
  children: ReactNode;
  /** App identifier used to persist org selection per-app (e.g. 'sales', 'catalog') */
  appId?: string;
  /** Which org to default to when no selection is stored. Defaults to 'prod'. */
  defaultOrg?: OrgId;
}

export function DataverseOrgProvider({ children, appId, defaultOrg = 'prod' }: DataverseOrgProviderProps) {
  const prodUrl = import.meta.env.VITE_DATAVERSE_URL ?? '';
  const devUrl = import.meta.env.VITE_DATAVERSE_URL_DEV ?? '';
  const hasDevOrg = Boolean(devUrl) && devUrl !== prodUrl;
  const storageKey = getStorageKey(appId);

  const [activeOrg] = useState<OrgId>(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored === 'dev' && hasDevOrg) return 'dev';
    if (stored === 'prod') return 'prod';
    // No stored preference — use the app's default
    return hasDevOrg ? defaultOrg : 'prod';
  });

  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [clientReady, setClientReady] = useState(false);

  // Token cache shared across all Dataverse calls for the active org
  const cachedToken = useRef<string | null>(null);
  const tokenExpiresAt = useRef<Date | null>(null);
  const refreshPromise = useRef<Promise<string> | null>(null);

  const createTokenProvider = useCallback(
    (org: OrgId) => {
      // Clear cache when org changes
      cachedToken.current = null;
      tokenExpiresAt.current = null;
      refreshPromise.current = null;

      return async (): Promise<string> => {
        // Return cached token if still fresh (>5 min remaining)
        const bufferMs = 5 * 60 * 1000;
        if (
          cachedToken.current &&
          tokenExpiresAt.current &&
          tokenExpiresAt.current.getTime() - Date.now() > bufferMs
        ) {
          return cachedToken.current;
        }

        // If a refresh is already in flight, wait for it (mutex)
        if (refreshPromise.current) {
          return refreshPromise.current;
        }

        // Start a new refresh — all concurrent callers share this promise
        refreshPromise.current = (async () => {
          try {
            const orgParam = org === 'dev' ? '?org=dev' : '';
            const response = await fetch(`/auth/token${orgParam}`, { credentials: 'include' });

            if (response.status === 401) {
              window.location.href = '/auth/login';
              throw new Error('Authentication required');
            }
            if (!response.ok) {
              throw new Error(`Token fetch failed: ${response.statusText}`);
            }

            const data = await response.json();
            cachedToken.current = data.accessToken;
            tokenExpiresAt.current = data.expiresOn ? new Date(data.expiresOn) : null;
            return data.accessToken as string;
          } finally {
            refreshPromise.current = null;
          }
        })();

        return refreshPromise.current;
      };
    },
    [],
  );

  const setActiveOrg = useCallback(
    (org: OrgId) => {
      if (org === activeOrg) return;
      localStorage.setItem(storageKey, org);
      // Full page reload ensures clean state — new token, fresh queries, no stale cache
      window.location.reload();
    },
    [activeOrg, storageKey],
  );

  // Initialize client for the active org — must complete before children render
  // to prevent React Query hooks from firing before the client is ready
  useEffect(() => {
    if (!isAuthenticated) {
      setClientReady(false);
      return;
    }

    const targetUrl = activeOrg === 'dev' && hasDevOrg ? devUrl : prodUrl;
    reinitializeDataverseClient(targetUrl, createTokenProvider(activeOrg));
    setClientReady(true);
  }, [isAuthenticated, activeOrg, hasDevOrg, devUrl, prodUrl, createTokenProvider]);

  const orgLabel = activeOrg === 'dev' && hasDevOrg ? getOrgLabel(devUrl) : getOrgLabel(prodUrl);

  const value = useMemo<OrgContextValue>(
    () => ({
      activeOrg,
      setActiveOrg,
      orgLabel,
      prodUrl,
      devUrl,
      hasDevOrg,
    }),
    [activeOrg, setActiveOrg, orgLabel, prodUrl, devUrl, hasDevOrg],
  );

  // Don't render children until the Dataverse client is initialized —
  // prevents React Query hooks from firing before the client is ready.
  // Exception: when the user is unauthenticated, render children anyway so
  // the router can navigate to /login (no Dataverse queries fire there).
  if (!clientReady && (authLoading || isAuthenticated)) return null;

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}
